import {
	type ApiErrorResponse,
	type CollectibleAuctionCosts,
	type FetchCollectibleAuctionBidsByAuctionIdData,
	type FetchCollectibleAuctionBidsByAuctionIdParams,
	type FetchCollectibleAuctionByAuctionIdData,
	type FetchCollectibleAuctionByAuctionIdParams,
	type FetchCollectibleAuctionCostsByAuctionIdData,
	type FetchCollectibleAuctionCostsByAuctionIdParams,
	type FetchUserDeliveryAddressesData,
	type FetchUserDeliveryAddressesParams,
	minutesToMs,
	type Pagination,
	type PaginationWithDetailedPaginationMode,
	type PostCollectibleAuctionBidByAuctionIdData,
	type PutCollectibleDeliveryByDeliveryIdPayload,
	userApi,
	type UserDeliveryAddress2,
	type UserDeliveryAddressDTO
} from '@capsicum/common';
import {
	type InfiniteData,
	useInfiniteQuery,
	type UseInfiniteQueryOptions,
	useMutation,
	useQuery,
	type UseQueryOptions
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { type BidCollectible, type CollectibleWithImage } from '../../models';
import { collectiblesApi } from '../api';

export const UseAuctionDetailsQueryKey = (scope: 'auctionDetails' | 'bidList' | 'shippingCost') => [scope];
export const UsePlaceBidQueryKey = ['placeBid'] as const;

export const useAuctionDetailsQueries = (
	params: FetchCollectibleAuctionByAuctionIdParams,
	options?: Omit<UseQueryOptions<FetchCollectibleAuctionByAuctionIdData, unknown, CollectibleWithImage>, 'queryFn'>
) => {
	return useQuery<FetchCollectibleAuctionByAuctionIdData, unknown, CollectibleWithImage>({
		enabled: !!params.auctionId && !!params.altCurrencyId && !!params.mainCurrencyId,
		queryFn: async () =>
			(await collectiblesApi).fetchCollectibleAuctionByAuctionId({
				'details[]': [
					'collectibleImages',
					'attributeTranslations',
					'collectibleTranslations',
					'collectibleAttributeTranslations',
					'tagTranslations'
				],
				...params
			} as FetchCollectibleAuctionByAuctionIdParams),
		queryKey: [UseAuctionDetailsQueryKey('auctionDetails'), params],
		select: res => res.data || {},
		staleTime: minutesToMs(10),
		...options
	});
};

export const useBidListQueries = (
	params: FetchCollectibleAuctionBidsByAuctionIdParams,
	options?: Omit<
		UseInfiniteQueryOptions<
			FetchCollectibleAuctionBidsByAuctionIdData,
			unknown,
			InfiniteData<Pagination & { data: Array<BidCollectible> }>
		>,
		'queryFn'
	>
) => {
	return useInfiniteQuery<FetchCollectibleAuctionBidsByAuctionIdData, unknown, Array<BidCollectible>>({
		...options,
		enabled: !!params.auctionId && !!params.altCurrencyId && !!params.mainCurrencyId,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithDetailedPaginationMode['metadata'];

			const currentPageCursor = metadata?.current_page || 1;
			const lastPageCursor = metadata?.last_page || 1;

			if (currentPageCursor < lastPageCursor) return currentPageCursor + 1;

			return undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) =>
			(await collectiblesApi).fetchCollectibleAuctionBidsByAuctionId({
				...params,
				page: pageParam as number | undefined
			} as FetchCollectibleAuctionByAuctionIdParams),
		queryKey: [UseAuctionDetailsQueryKey('bidList'), params],
		select: res => res.pages.flatMap(page => page.data || []),
		staleTime: minutesToMs(10)
	});
};

export const useUserDeliveryAddressQueries = (
	params: FetchUserDeliveryAddressesParams,
	options?: Omit<UseQueryOptions<FetchUserDeliveryAddressesData, unknown, Array<UserDeliveryAddress2>>, 'queryFn'>
) => {
	return useQuery<FetchUserDeliveryAddressesData, unknown, Array<UserDeliveryAddress2>>({
		queryFn: async () =>
			(await userApi).fetchUserDeliveryAddresses({
				...params
			} as FetchUserDeliveryAddressesParams),
		queryKey: ['userDeliveryAddresses', params],
		select: res => res.data || {},
		staleTime: minutesToMs(10),
		...options
	});
};

type UsePutUserDeliveryAddressProps = {
	deliveryAddressData: UserDeliveryAddressDTO;
	deliveryAddressId: string;
};

export const usePutUserDeliveryAddressQueries = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async ({ deliveryAddressData, deliveryAddressId }: UsePutUserDeliveryAddressProps) =>
			(await userApi).putUserDeliveryAddressByDeliveryAddressId(deliveryAddressId, deliveryAddressData),
		mutationKey: ['userDeliveryAddressesPut']
	});

	return {
		data,
		error,
		isPending,
		isSuccess,
		mutateAsync
	};
};

type UsePostUserDeliveryAddressProps = {
	deliveryAddressData: UserDeliveryAddressDTO;
};

export const usePostUserDeliveryAddressQueries = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async ({ deliveryAddressData }: UsePostUserDeliveryAddressProps) =>
			(await userApi).postUserDeliveryAddress(deliveryAddressData),
		mutationKey: ['userDeliveryAddressesPost']
	});

	return {
		data,
		error,
		isPending,
		isSuccess,
		mutateAsync
	};
};

type PostBidParams = {
	altCurrencyId: string;
	auctionId: string;
	bidAmount: string;
	bidCurrencyId: string;
	currencyNetworkId: string;
	mainCurrencyId: string;
	shippingCountryCode: string;
};

export const usePostBidQueries = () =>
	useMutation<PostCollectibleAuctionBidByAuctionIdData, AxiosError<ApiErrorResponse>, PostBidParams, unknown>({
		mutationFn: async ({
			altCurrencyId,
			auctionId,
			bidAmount,
			bidCurrencyId,
			currencyNetworkId,
			mainCurrencyId,
			shippingCountryCode
		}: PostBidParams) =>
			(await collectiblesApi).postCollectibleAuctionBidByAuctionId(
				{
					altCurrencyId,
					auctionId,
					mainCurrencyId
				},
				{
					bidAmount,
					bidCurrencyId,
					currencyNetworkId,
					shippingCountryCode
				},
				{ customErrorInterceptor: () => true }
			),
		mutationKey: UsePlaceBidQueryKey
	});

type PutCollectibleDeliveryByDeliveryIdParams = {
	deliveryAddressData: PutCollectibleDeliveryByDeliveryIdPayload;
	deliveryId: string;
};

export const usePutCollectibleDeliveryByDeliveryId = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async ({ deliveryAddressData, deliveryId }: PutCollectibleDeliveryByDeliveryIdParams) =>
			(await collectiblesApi).putCollectibleDeliveryByDeliveryId(deliveryId, deliveryAddressData),
		mutationKey: ['collectibleDeliveryByDeliveryIdPut']
	});

	return {
		data,
		error,
		isPending,
		isSuccess,
		mutateAsync
	};
};

export const useFetchCollectibleAuctionCostsByAuctionId = (
	params: FetchCollectibleAuctionCostsByAuctionIdParams,
	options?: Omit<
		UseQueryOptions<FetchCollectibleAuctionCostsByAuctionIdData, unknown, CollectibleAuctionCosts>,
		'queryFn'
	>
) => {
	return useQuery<FetchCollectibleAuctionCostsByAuctionIdData, unknown, CollectibleAuctionCosts>({
		enabled: params?.bidAmount ? Number(params?.bidAmount) > 0 : false,
		queryFn: async () =>
			(await collectiblesApi).fetchCollectibleAuctionCostsByAuctionId({
				...(params.shippingCountryCode ? { shippingCountryCode: params.shippingCountryCode } : {}),
				...params
			} as FetchCollectibleAuctionCostsByAuctionIdParams),
		queryKey: ['useFetchCollectibleAuctionCostsByAuctionId', params],
		select: res => res.data || {},
		staleTime: minutesToMs(10),
		...options
	});
};
